// JivoSettingsDialog — правка настроек JivoChat (Tech-Story-jivo-admin-settings, R-FE-2;
// канон SmtpAccountDialog, сам SmtpAccountDialog не правится). Секрет write-only: поле
// при открытии всегда пустое, placeholder-маска при hasSecret, пустое поле = «оставить
// прежний» (в PUT не уходит). widgetId/crmBaseUrl пустые → null (fallback на env).

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { jivoSettingsApi, JIVO_SECRET_MASK, type JivoSettingsAdmin } from '@/api/jivoSettings';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

interface JivoSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Текущие настройки (null — GET ещё не отработал, диалог не открывать). */
  settings: JivoSettingsAdmin | null;
  /** Колбэк успешного сохранения (страница показывает newWebhookSecret один раз). */
  onSaved: (updated: JivoSettingsAdmin) => void;
}

/** Диалог настроек JivoChat: форма + PUT-мутация (пустой секрет — не менять). */
export function JivoSettingsDialog({ open, onClose, settings, onSaved }: JivoSettingsDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [widgetId, setWidgetId] = useState('');
  const [crmBaseUrl, setCrmBaseUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [webhookSecret, setWebhookSecret] = useState('');

  // Сброс формы под текущие настройки при каждом открытии; секрет всегда пустой (write-only).
  useEffect(() => {
    if (!open) return;
    setWidgetId(settings?.widgetId ?? '');
    setCrmBaseUrl(settings?.crmBaseUrl ?? '');
    setEnabled(settings?.enabled ?? true);
    setWebhookSecret('');
  }, [open, settings]);

  const saveMutation = useMutation({
    mutationFn: () =>
      jivoSettingsApi.update({
        enabled,
        widgetId: widgetId.trim() !== '' ? widgetId.trim() : null,
        crmBaseUrl: crmBaseUrl.trim() !== '' ? crmBaseUrl.trim() : null,
        // Пустой секрет — «не менять» (канон SmtpAccountDialog §3.5).
        ...(webhookSecret.length > 0 ? { webhookSecret } : {}),
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['jivo', 'settings'] });
      onSaved(updated);
      onClose();
    },
  });

  const saveError = saveMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(saveMutation.error, t('admin.jivo.saveError'))}</Alert>
  ) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('admin.jivo.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveError}
          <TextField
            label={t('admin.jivo.widgetId')}
            fullWidth
            value={widgetId}
            onChange={(e) => setWidgetId(e.target.value)}
            inputProps={{ 'aria-label': t('admin.jivo.widgetId') }}
          />
          <TextField
            label={t('admin.jivo.crmBaseUrl')}
            fullWidth
            value={crmBaseUrl}
            onChange={(e) => setCrmBaseUrl(e.target.value)}
            inputProps={{ 'aria-label': t('admin.jivo.crmBaseUrl') }}
          />
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label={t('admin.jivo.enabled')}
          />
          <Typography variant="caption" color="text.secondary">
            {t('admin.jivo.enabledDisableWarning')}
          </Typography>
          <TextField
            label={t('admin.jivo.secret')}
            fullWidth
            type="password"
            // Маска: секрет не отображается; при hasSecret — placeholder «••••••••» + подсказка.
            placeholder={settings?.hasSecret ? JIVO_SECRET_MASK : ''}
            helperText={settings?.hasSecret ? t('admin.jivo.secretHint') : undefined}
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            inputProps={{ 'aria-label': t('admin.jivo.secret') }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
